import { Button } from "@/components/generic/Button";
import { CheckCard } from "@/components/generic/CheckCard";
import { Input } from "@/components/generic/Field";
import { Heading } from "@/components/generic/Heading";
import { Modal } from "@/components/generic/Modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { CloudCheckIcon } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

const uploadSchema = z.object({
  name: z.string().min(1, "Please enter a name for the stop list"),
  mandatory: z.boolean(),
  file: z
    .custom<File>()
    .refine((file) => file instanceof File, "Please select a file to upload"),
});

type UploadFormData = z.infer<typeof uploadSchema>;

export default function UploadStopListModal({
  isOpen,
  onClose,
  file,
  name,
  setName,
  mandatory,
  setMandatory,
  onSave,
  showErrorModal,
  setShowErrorModal,
  validCount,
}: {
  isOpen: boolean;
  onClose: () => void;
  file: File | null;
  name: string;
  setName: (name: string) => void;
  mandatory: boolean;
  setMandatory: (checked: boolean) => void;
  onSave: () => void;
  showErrorModal?: boolean;
  setShowErrorModal?: (show: boolean) => void;
  validCount: number;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
  } = useForm<UploadFormData>({
    resolver: zodResolver(uploadSchema),
    mode: "onChange",
    defaultValues: {
      name,
      mandatory: true,
      file: file || undefined,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: UploadFormData) => {
    setName(data.name);
    setMandatory(data.mandatory);
    onSave();
    reset();
  };

  useEffect(() => {
    if (file) {
      setValue("file", file);
    }
  }, [file, setValue]);

  return (
    <>
      <Modal
        isDismissable
        isOpen={isOpen}
        onOpenChange={handleClose}
        size="lg"
        icon={
          <CloudCheckIcon size={40} className="text-theme-gradient-from/90" />
        }
      >
        <div className="relative">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col items-center justify-center gap-6">
              <div className="flex w-full flex-col items-center gap-4">
                <span className="text-md text-theme-on-glass font-medium">
                  Opret stopliste
                </span>
                <div className="text-center">
                  <span className="text-theme-on-glass-variant text-sm font-medium">
                    {validCount > 0 && `${validCount} firmaer`}
                    {file?.name
                      ? " fundet i den valgte fil."
                      : "Ingen fil valgt"}
                    <br />
                    Navngiv venligst din stopliste for at oprette.
                  </span>
                  {errors.file && (
                    <div className="mt-2">
                      <span className="text-xs text-red-400">
                        {errors.file.message}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="File name"
                      className={`w-full max-w-full ${
                        errors.name ? "border-red-400 focus:border-red-400" : ""
                      }`}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setName(e.target.value);
                      }}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                {errors.name && (
                  <div className="mt-1">
                    <span className="text-xs text-red-400">
                      {errors.name.message}
                    </span>
                  </div>
                )}
              </div>

              <CheckCard
                key="stoplist-name"
                control={control}
                name="mandatory"
                value="stoplist-name"
                label="Aktiver for alle kampagner"
                onChange={(checked) => {
                  setMandatory(checked);
                }}
              />

              <div className="flex gap-4">
                <Button
                  type="button"
                  intent="secondary"
                  animate
                  className="px-6 py-3 text-sm font-medium"
                  onClick={handleClose}
                >
                  Fortryd
                </Button>
                <Button
                  type="submit"
                  animate
                  className={`px-6 py-3 text-sm font-medium ${
                    !isValid ? "cursor-not-allowed opacity-50" : ""
                  }`}
                  isDisabled={!isValid}
                >
                  Opret
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Modal>

      <Modal
        isOpen={showErrorModal}
        onOpenChange={() => setShowErrorModal?.(false)}
        size="sm"
      >
        <div className="flex flex-col items-center gap-6">
          <Heading className="text-md font-medium">Ugyldigt filformat</Heading>
          <p className="text-theme-on-glass-variant text-center text-sm">
            Den valgte fil var desværre i et forkert format. Filen skal
            indeholde 8-cifrede CVR-numre, i en kolonne med titlen "cvr"
          </p>
          <Button
            animate
            className="px-6 py-3 text-sm font-medium"
            onClick={() => {
              setShowErrorModal?.(false);
              handleClose();
            }}
          >
            OK
          </Button>
        </div>
      </Modal>
    </>
  );
}
